import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowRight, Briefcase, CheckCircle, Clock, XCircle, Bookmark, MapPin, Target, Banknote } from 'lucide-react';
import CandidateLayout from '../components/CandidateLayout';
import TalkToDavid from '../components/chat/TalkToDavid';
import ResumeUpload from '../components/resume/ResumeUpload';
import DashboardJobCard from '../components/dashboard/DashboardJobCard';
import { useAuthProtection } from '../hooks/useAuthProtection';
import { candidateServiceApi } from '../services/candidateServiceApi';
import { savedJobsApi } from '../services/savedJobsApi';
import { applicationApiService } from '../services/applicationApi';
import type { CandidateProfile } from '../services/candidateServiceApi';
import type { Job } from '../../recruiter/services/jobApi';
// Profile strength removed per meeting decision

/**
 * Main Candidate Dashboard Page
 * Displays comprehensive overview of candidate's job search progress
 * Route: /candidate/dashboard
 */
const Dashboard: React.FC = () => {
    // Protect route - redirect to login if not authenticated
    useAuthProtection();

    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [activeView, setActiveView] = useState<'dashboard' | 'chat' | 'resume-upload'>('dashboard');
    const [userName, setUserName] = useState('');
    const [candidateProfile, setCandidateProfile] = useState<CandidateProfile | null>(null);
    const [isLoadingProfile, setIsLoadingProfile] = useState(true);

    // Search state for header
    const [dashboardSearchInput, setDashboardSearchInput] = useState('');

    // Job statistics time period
    const [statsPeriod, setStatsPeriod] = useState<'Week' | 'Month' | 'Year'>('Week');

    const handleDashboardSearchSubmit = () => {
        const trimmed = dashboardSearchInput.trim();
        if (trimmed) {
            navigate(`/candidate/jobs?q=${encodeURIComponent(trimmed)}`);
            setDashboardSearchInput('');
        }
    };

    // My Jobs state
    const [savedJobs, setSavedJobs] = useState<Job[]>([]);
    const [appliedJobs, setAppliedJobs] = useState<{ jobId: string; job: Job }[]>([]);
    const [isLoadingJobs, setIsLoadingJobs] = useState(true);
    const [myJobsFilter, setMyJobsFilter] = useState<'all' | 'saved' | 'applied'>('all');

    // Get user data and fetch candidate profile
    useEffect(() => {
        const fetchCandidateData = async () => {
            const userDataString = localStorage.getItem('authUser');
            if (userDataString) {
                try {
                    const userData = JSON.parse(userDataString);
                    const firstName = userData.first_name || userData.firstName || userData.name || userData.email?.split('@')[0] || 'Ben';
                    setUserName(firstName);

                    // Fetch candidate profile using /me endpoint (JWT-based auth)
                    try {
                        const response = await candidateServiceApi.getCandidateProfile();
                        if (response.success && response.candidate) {
                            setCandidateProfile(response.candidate);

                            // Update user info from profile if available
                            if (response.candidate.name) {
                                const profileFirstName = response.candidate.name.split(' ')[0];
                                setUserName(profileFirstName);
                            }
                        }
                    } catch (error) {
                        console.error('Failed to fetch candidate profile:', error);
                    }
                } catch (error) {
                    console.error('Failed to parse user data:', error);
                } finally {
                    setIsLoadingProfile(false);
                }
            } else {
                setIsLoadingProfile(false);
            }
        };

        fetchCandidateData();
    }, []);

    // Fetch saved jobs and applications for My Jobs section
    useEffect(() => {
        const fetchMyJobs = async () => {
            setIsLoadingJobs(true);
            try {
                // Fetch saved jobs and applications in parallel
                const [savedResponse, applicationsResponse] = await Promise.all([
                    savedJobsApi.getSavedJobs().catch(() => ({ payload: { data: [] } })),
                    applicationApiService.getMyApplications().catch(() => ({ payload: { data: [] }, data: [] }))
                ]);

                // Extract saved jobs
                const savedJobsData = savedResponse.payload?.data || [];
                setSavedJobs(savedJobsData);

                // Extract applied jobs with job details
                const applicationsData = applicationsResponse.payload?.data || applicationsResponse.data || [];
                // Map applications to job-like format for display
                // Application has jobId, jobTitle, companyName directly (not nested job object)
                const appliedJobsData = applicationsData.map((app: any) => ({
                    jobId: app.jobId,
                    job: {
                        id: app.jobId,
                        _id: app.jobId,
                        title: app.jobTitle || 'Unknown Position',
                        companyName: app.companyName || 'Unknown Company',
                        location: app.location || '',
                        // Include any nested job data if available
                        ...(app.job || app.jobDetails || {})
                    }
                }));
                setAppliedJobs(appliedJobsData);

                console.log('My Jobs loaded:', {
                    saved: savedJobsData.length,
                    applied: appliedJobsData.length
                });
            } catch (error) {
                console.error('Failed to fetch my jobs:', error);
            } finally {
                setIsLoadingJobs(false);
            }
        };

        fetchMyJobs();
    }, []);

    // Handle view job in browse jobs page
    const handleViewJobInBrowse = (jobId: string) => {
        navigate(`/candidate/jobs?highlight=${jobId}`);
    };

    // Check URL params for view
    useEffect(() => {
        const view = searchParams.get('view');
        if (view === 'chat') {
            setActiveView('chat');
        } else if (view === 'resume-upload') {
            setActiveView('resume-upload');
        } else {
            setActiveView('dashboard');
        }
    }, [searchParams]);

    // Render chat view
    if (activeView === 'chat') {
        return (
            <CandidateLayout showHeader={false}>
                <div className="mt-4 lg:mt-8 h-[calc(100vh-80px)] lg:h-[calc(100vh-60px)] bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <TalkToDavid />
                </div>
            </CandidateLayout>
        );
    }

    // Render resume upload view
    if (activeView === 'resume-upload') {
        return (
            <CandidateLayout
                headerTitle="Upload Resume"
                headerSubtitle="Upload your resume to get started with your profile"
            >
                <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
                    <ResumeUpload onComplete={(candidateId) => {
                        console.log('Resume uploaded successfully for candidate:', candidateId);
                        navigate('/candidate/dashboard');
                    }} />
                </div>
            </CandidateLayout>
        );
    }

    // Render dashboard view
    return (
        <CandidateLayout
            isHomePage={true}
            searchConfig={{
                placeholder: "Search for jobs...",
                value: dashboardSearchInput,
                onChange: setDashboardSearchInput,
                onSubmit: handleDashboardSearchSubmit,
            }}
        >
            {/* Main Content Grid */}
            <div className="flex flex-col xl:flex-row gap-6">
                {/* Left Column */}
                <div className="flex-1 space-y-6 min-w-0">
                    {/* Resume Completion Card */}
                    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100/80 relative overflow-hidden group hover:shadow-md transition-all duration-300">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-[#015FA6]/5 to-transparent rounded-bl-full -mr-16 -mt-16 transition-transform duration-500 group-hover:scale-110"></div>

                        <div className="flex flex-col md:flex-row items-center gap-6 md:gap-8 relative z-10">
                            {(() => {
                                // Get first letter for avatar
                                const firstName = candidateProfile?.firstName || userName.split(' ')[0] || userName || 'U';
                                const avatarLetter = firstName ? firstName.charAt(0).toUpperCase() : 'U';

                                return (
                                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-[#015FA6] to-[#6EABFF] flex items-center justify-center shadow-lg shadow-blue-500/20 flex-shrink-0 ring-4 ring-white">
                                        <span className="text-white font-bold text-3xl md:text-4xl leading-none">
                                            {avatarLetter}
                                        </span>
                                    </div>
                                );
                            })()}

                            <div className="flex-1 text-center md:text-left w-full">
                                {isLoadingProfile ? (
                                    <div className="animate-pulse space-y-3">
                                        <div className="h-6 bg-gray-100 rounded-lg w-3/4 mx-auto md:mx-0"></div>
                                        <div className="h-4 bg-gray-100 rounded-lg w-full mx-auto md:mx-0"></div>
                                    </div>
                                ) : candidateProfile?.parsedResume ? (
                                    <>
                                        {(() => {
                                            const firstName = candidateProfile.firstName || userName.split(' ')[0] || '';
                                            const capitalizedFirstName = firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();

                                            return (
                                                <>
                                                    <h2 className="text-2xl md:text-3xl font-bold text-[#0B111E] mb-2">
                                                        Welcome back, {capitalizedFirstName}
                                                    </h2>
                                                    <p className="text-sm font-medium text-[#757575]">
                                                        Resume uploaded. Keep your profile updated to get the best job matches.
                                                    </p>
                                                </>
                                            );
                                        })()}
                                    </>
                                ) : (
                                    <>
                                        <h2 className="text-2xl md:text-3xl font-bold text-[#0B111E] mb-2">
                                            Get started with your profile
                                        </h2>
                                        <p className="text-sm font-medium text-[#757575]">
                                            Upload your resume to get started with your profile.
                                        </p>
                                    </>
                                )}
                            </div>

                            <div className="flex flex-row md:flex-col gap-3 w-full md:w-auto min-w-[140px]">
                                <button
                                    onClick={() => navigate('/candidate/dashboard?view=resume-upload')}
                                    className="flex-1 px-6 py-3 text-sm font-bold text-white bg-[#015FA6] rounded-xl hover:bg-[#014a85] hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 active:scale-95"
                                >
                                    {candidateProfile?.parsedResume ? 'Replace Resume' : 'Upload Resume'}
                                </button>
                                <button
                                    onClick={() => navigate('/candidate/my-profile')}
                                    className="flex-1 px-6 py-3 text-sm font-bold text-[#015FA6] bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-all duration-300 active:scale-95"
                                >
                                    {candidateProfile?.parsedResume ? 'View Profile' : 'Complete Profile'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Job Statistics */}
                    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100/80">
                        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-8 gap-4">
                            <div>
                                <h3 className="text-xl font-bold text-[#0B111E]">Job statistics</h3>
                                <p className="text-sm font-medium text-[#757575] mt-1">Showing Job statistics this {statsPeriod.toLowerCase()}</p>
                            </div>
                            <div className="flex bg-gray-50 p-1 rounded-xl border border-gray-100">
                                {(['Week', 'Month', 'Year'] as const).map((period) => (
                                    <button
                                        key={period}
                                        onClick={() => setStatsPeriod(period)}
                                        className={`px-4 py-2 text-sm font-medium transition-all ${
                                            statsPeriod === period
                                                ? 'font-bold text-white bg-[#015FA6] rounded-lg shadow-sm'
                                                : 'text-[#757575] hover:text-[#015FA6]'
                                        }`}
                                    >
                                        {period}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Chart Area - Empty State with minimal bars */}
                        <div className="h-[280px] relative bg-gradient-to-b from-transparent to-gray-50/50 rounded-2xl border border-gray-100/50 p-4">
                            {/* No data overlay */}
                            <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                                <div className="text-center bg-white/80 backdrop-blur-sm px-8 py-6 rounded-2xl shadow-lg border border-white/50">
                                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-50 rounded-full flex items-center justify-center">
                                        <Briefcase className="w-8 h-8 text-gray-300" />
                                    </div>
                                    <p className="text-base font-bold text-[#0B111E] mb-1">No activity data yet</p>
                                    <p className="text-sm text-[#757575]">Start browsing jobs to see statistics</p>
                                </div>
                            </div>

                            {/* Minimal bars for design */}
                            <div className="h-full flex items-end gap-3 opacity-30 px-4 pb-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                                    <div key={day} className="flex-1 flex flex-col items-center justify-end h-full group">
                                        <div
                                            className="w-full bg-gradient-to-t from-[#015FA6]/30 to-[#015FA6]/5 rounded-t-xl transition-all duration-500 group-hover:from-[#015FA6]/40"
                                            style={{ height: `${15 + Math.random() * 30}%` }}
                                        ></div>
                                        <span className="text-xs font-medium text-[#A5A5A5] mt-3">{day}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Legend */}
                        <div className="flex gap-6 mt-6 justify-center sm:justify-start">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-[#015FA6] rounded-full ring-2 ring-blue-100"></div>
                                <span className="text-sm font-medium text-[#515151]">Job views</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 bg-[#6EABFF] rounded-full ring-2 ring-blue-50"></div>
                                <span className="text-sm font-medium text-[#515151]">Job Applied</span>
                            </div>
                        </div>
                    </div>

                    {/* My Jobs */}
                    <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100/80">
                        <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
                            <div className="flex items-center gap-3">
                                <h3 className="text-xl font-bold text-[#0B111E]">My jobs</h3>
                                {(savedJobs.length > 0 || appliedJobs.length > 0) && (
                                    <span className="px-2 py-0.5 text-xs font-bold text-[#015FA6] bg-[#E3F2FD] rounded-full">
                                        {savedJobs.length + appliedJobs.length}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => navigate('/candidate/jobs')}
                                className="text-sm font-bold text-[#015FA6] flex items-center gap-1 hover:gap-2 transition-all group"
                            >
                                Browse Jobs
                                <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
                            </button>
                        </div>

                        {/* Filter Tabs */}
                        {(savedJobs.length > 0 || appliedJobs.length > 0) && (
                            <div className="flex gap-2 mb-4">
                                <button
                                    onClick={() => setMyJobsFilter('all')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        myJobsFilter === 'all'
                                            ? 'bg-[#015FA6] text-white'
                                            : 'bg-[#F1F3F4] text-[#515151] hover:bg-[#E3F2FD]'
                                    }`}
                                >
                                    All ({savedJobs.length + appliedJobs.length})
                                </button>
                                <button
                                    onClick={() => setMyJobsFilter('saved')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        myJobsFilter === 'saved'
                                            ? 'bg-[#015FA6] text-white'
                                            : 'bg-[#F1F3F4] text-[#515151] hover:bg-[#E3F2FD]'
                                    }`}
                                >
                                    Saved ({savedJobs.length})
                                </button>
                                <button
                                    onClick={() => setMyJobsFilter('applied')}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                        myJobsFilter === 'applied'
                                            ? 'bg-[#015FA6] text-white'
                                            : 'bg-[#F1F3F4] text-[#515151] hover:bg-[#E3F2FD]'
                                    }`}
                                >
                                    Applied ({appliedJobs.length})
                                </button>
                            </div>
                        )}

                        {isLoadingJobs ? (
                            // Loading State
                            <div className="py-12 text-center">
                                <div className="w-8 h-8 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                                <p className="text-sm text-[#757575]">Loading your jobs...</p>
                            </div>
                        ) : savedJobs.length === 0 && appliedJobs.length === 0 ? (
                            // Empty State
                            <div className="py-16 text-center bg-gray-50/50 rounded-2xl border border-dashed border-gray-200">
                                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-white shadow-sm flex items-center justify-center ring-4 ring-gray-50">
                                    <Bookmark className="w-10 h-10 text-gray-300" />
                                </div>
                                <p className="text-lg font-bold text-[#0B111E] mb-2">No saved jobs yet</p>
                                <p className="text-sm text-[#757575] mb-8 max-w-xs mx-auto">Browse and save jobs you're interested in to track them here</p>
                                <button
                                    onClick={() => navigate('/candidate/jobs')}
                                    className="px-8 py-3 text-sm font-bold text-white bg-[#015FA6] rounded-xl hover:bg-[#014a85] hover:shadow-lg hover:shadow-blue-500/20 transition-all duration-300 active:scale-95"
                                >
                                    Start Browsing
                                </button>
                            </div>
                        ) : (
                            // Jobs List
                            <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
                                {/* Applied jobs - show when filter is 'all' or 'applied' */}
                                {(myJobsFilter === 'all' || myJobsFilter === 'applied') && appliedJobs.map((app) => (
                                    <DashboardJobCard
                                        key={`applied-${app.jobId}`}
                                        job={app.job}
                                        status="applied"
                                        onViewClick={handleViewJobInBrowse}
                                    />
                                ))}
                                {/* Saved jobs - show when filter is 'all' or 'saved' */}
                                {(myJobsFilter === 'all' || myJobsFilter === 'saved') && savedJobs.map((job) => (
                                    <DashboardJobCard
                                        key={`saved-${job.id || job._id}`}
                                        job={job}
                                        status="saved"
                                        onViewClick={handleViewJobInBrowse}
                                    />
                                ))}
                                {/* Empty state for filtered view */}
                                {myJobsFilter === 'saved' && savedJobs.length === 0 && (
                                    <div className="py-8 text-center">
                                        <Bookmark className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                        <p className="text-sm text-[#757575]">No saved jobs yet</p>
                                    </div>
                                )}
                                {myJobsFilter === 'applied' && appliedJobs.length === 0 && (
                                    <div className="py-8 text-center">
                                        <CheckCircle className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                                        <p className="text-sm text-[#757575]">No applied jobs yet</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Hidden on mobile */}
                <div className="hidden xl:block w-[400px] space-y-6 flex-shrink-0">
                    {/* Status Pie Chart */}
                    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100/80 h-auto sticky top-28">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-xl font-bold text-[#0B111E]">Application Status</h3>
                        </div>
                        <p className="text-sm font-medium text-[#757575] mb-8">
                            {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                        </p>

                        {/* Empty State with Pie Chart Design */}
                        <div className="flex flex-col items-center gap-8">
                            {/* Empty Pie Chart */}
                            <div className="w-[240px] h-[240px] rounded-full relative flex items-center justify-center bg-gray-50 border-8 border-gray-100 shadow-inner">
                                <div className="w-[160px] h-[160px] rounded-full bg-white flex items-center justify-center flex-col shadow-sm">
                                    <span className="text-4xl font-bold text-gray-300">0</span>
                                    <span className="text-xs font-bold text-gray-400 uppercase tracking-wider mt-1">Total jobs</span>
                                </div>

                                {/* Decorative circles */}
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-300 rounded-full"></div>
                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 -mb-1 w-2 h-2 bg-gray-300 rounded-full"></div>
                            </div>

                            {/* Legend */}
                            <div className="w-full space-y-4">
                                <div className="flex justify-between items-center p-3 rounded-xl hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#015FA6]">
                                            <Clock size={20} />
                                        </div>
                                        <span className="text-sm font-bold text-[#515151]">Applied</span>
                                    </div>
                                    <span className="text-sm font-bold text-gray-400">0</span>
                                </div>
                                <div className="flex justify-between items-center p-3 rounded-xl hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                                            <CheckCircle size={20} />
                                        </div>
                                        <span className="text-sm font-bold text-[#515151]">Offer</span>
                                    </div>
                                    <span className="text-sm font-bold text-gray-400">0</span>
                                </div>
                                <div className="flex justify-between items-center p-3 rounded-xl hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-red-500">
                                            <XCircle size={20} />
                                        </div>
                                        <span className="text-sm font-bold text-[#515151]">Rejected</span>
                                    </div>
                                    <span className="text-sm font-bold text-gray-400">0</span>
                                </div>
                            </div>

                            <div className="w-full pt-4 border-t border-gray-100 text-center">
                                <p className="text-xs font-medium text-[#A5A5A5]">Apply to jobs to see your status updates</p>
                            </div>
                        </div>
                    </div>

                    {/* Career Preferences Summary */}
                    {candidateProfile?.preferences && (
                        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100/80">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-bold text-[#0B111E]">Career Preferences</h3>
                                <button
                                    onClick={() => navigate('/candidate/my-profile')}
                                    className="text-xs font-bold text-[#015FA6] hover:underline"
                                >
                                    Edit
                                </button>
                            </div>
                            <div className="space-y-3">
                                {candidateProfile.preferences.jobTitles?.length > 0 && (
                                    <div className="flex items-start gap-3">
                                        <Target size={16} className="text-[#015FA6] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs font-medium text-[#7C8493] mb-1">Target Roles</p>
                                            <div className="flex flex-wrap gap-1">
                                                {candidateProfile.preferences.jobTitles.slice(0, 3).map((title: string, i: number) => (
                                                    <span key={i} className="px-2 py-0.5 bg-[#E3F2FD] text-[#015FA6] text-xs font-medium rounded-md">
                                                        {title}
                                                    </span>
                                                ))}
                                                {candidateProfile.preferences.jobTitles.length > 3 && (
                                                    <span className="px-2 py-0.5 text-[#7C8493] text-xs">
                                                        +{candidateProfile.preferences.jobTitles.length - 3} more
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {candidateProfile.preferences.cities?.length > 0 && (
                                    <div className="flex items-start gap-3">
                                        <MapPin size={16} className="text-[#015FA6] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs font-medium text-[#7C8493] mb-1">Preferred Locations</p>
                                            <p className="text-sm text-[#515151]">
                                                {candidateProfile.preferences.cities.slice(0, 3).join(', ')}
                                                {candidateProfile.preferences.cities.length > 3 && ` +${candidateProfile.preferences.cities.length - 3} more`}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {(candidateProfile.preferences.minSalary || candidateProfile.preferences.maxSalary) && (
                                    <div className="flex items-start gap-3">
                                        <Banknote size={16} className="text-[#015FA6] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs font-medium text-[#7C8493] mb-1">Compensation</p>
                                            <p className="text-sm text-[#515151]">
                                                {candidateProfile.preferences.minSalary && candidateProfile.preferences.maxSalary
                                                    ? `$${Number(candidateProfile.preferences.minSalary).toLocaleString()} - $${Number(candidateProfile.preferences.maxSalary).toLocaleString()}`
                                                    : candidateProfile.preferences.minSalary
                                                        ? `From $${Number(candidateProfile.preferences.minSalary).toLocaleString()}`
                                                        : `Up to $${Number(candidateProfile.preferences.maxSalary).toLocaleString()}`
                                                }
                                            </p>
                                        </div>
                                    </div>
                                )}
                                {candidateProfile.preferences.workType && (
                                    <div className="flex items-start gap-3">
                                        <Briefcase size={16} className="text-[#015FA6] mt-0.5 flex-shrink-0" />
                                        <div>
                                            <p className="text-xs font-medium text-[#7C8493] mb-1">Work Type</p>
                                            <p className="text-sm text-[#515151]">{candidateProfile.preferences.workType}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {!candidateProfile.preferences.jobTitles?.length &&
                             !candidateProfile.preferences.cities?.length &&
                             !candidateProfile.preferences.minSalary &&
                             !candidateProfile.preferences.workType && (
                                <div className="text-center py-4">
                                    <p className="text-sm text-[#7C8493] mb-2">No preferences set yet</p>
                                    <button
                                        onClick={() => navigate('/candidate/my-profile')}
                                        className="text-sm font-bold text-[#015FA6] hover:underline"
                                    >
                                        Set Preferences
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </CandidateLayout>
    );
};

export default Dashboard;
